import React from "react";
import { Link } from "react-router-dom";
import "../css/delivery.scss";

const deliveryFacts = [
  {
    label: "Processing",
    value: "1–3 business days",
    detail: "Most orders are prepared and handed to a tracked carrier within three business days.",
  },
  {
    label: "International",
    value: "Tracked service",
    detail: "Available destinations and tracking are confirmed during checkout.",
  },
  {
    label: "Shipping cost",
    value: "$9.99",
    detail: "Standard tracked shipping is a fixed USD $9.99 per order.",
  },
];

function Delivery() {
  return (
    <main className="ink-info-page ink-info-page--delivery">
      <div className="ink-info-page__inner">
        <header className="ink-info-page__header">
          <p className="ink-info-page__eyebrow">From our desk to yours</p>
          <h1>Delivery &amp; Shipping</h1>
          <p className="ink-info-page__lead">
            ioomparade offers tracked delivery to the destinations listed at
            checkout for a fixed USD $9.99 shipping fee.
          </p>
          <p className="policy-updated">
            Effective and last updated: <time dateTime="2026-10-16">October 16, 2026</time>
          </p>
        </header>

        <section className="delivery-facts" aria-label="Delivery at a glance">
          {deliveryFacts.map((fact) => (
            <article className="delivery-fact" key={fact.label}>
              <p>{fact.label}</p>
              <h2>{fact.value}</h2>
              <span>{fact.detail}</span>
            </article>
          ))}
        </section>

        <div className="delivery-content">
          <section className="delivery-process" aria-labelledby="delivery-process-title">
            <p className="ink-info-page__kicker">What to expect</p>
            <h2 id="delivery-process-title">Your order&apos;s journey</h2>
            <ol>
              <li>
                <span aria-hidden="true">01</span>
                <div>
                  <h3>Order received</h3>
                  <p>
                    The fixed $9.99 shipping charge and complete order total are
                    shown before you continue to the secure payment service.
                  </p>
                </div>
              </li>
              <li>
                <span aria-hidden="true">02</span>
                <div>
                  <h3>Packed and handed off</h3>
                  <p>
                    Orders are normally passed to a tracked carrier within 1–3
                    business days. Orders placed over a weekend begin processing
                    on the next business day in Hong Kong.
                  </p>
                </div>
              </li>
              <li>
                <span aria-hidden="true">03</span>
                <div>
                  <h3>On the way</h3>
                  <p>
                    Tracking details are sent to the checkout email after dispatch.
                    Transit time depends on the destination and local delivery network.
                  </p>
                </div>
              </li>
            </ol>
          </section>

          <aside className="delivery-international" aria-labelledby="international-title">
            <p className="ink-info-page__kicker">Worldwide delivery</p>
            <h2 id="international-title">International orders</h2>
            <p>
              ioomparade ships only to destinations available in the checkout
              country selector. Restricted destinations are not offered.
            </p>
            <p>
              Customs or local carrier checks can extend transit time. Any duties
              or taxes required by the destination are the recipient&apos;s responsibility.
            </p>
            <div className="delivery-international__note">
              <strong>Please note</strong>
              <span>
                The carrier service, shipping price, and order total are presented
                at checkout before payment is completed.
              </span>
            </div>
          </aside>
        </div>

        <section className="commerce-policy-details" aria-labelledby="delivery-details-title">
          <div className="commerce-policy-details__heading">
            <p className="ink-info-page__kicker">Important details</p>
            <h2 id="delivery-details-title">Shipping terms</h2>
          </div>
          <div className="commerce-policy-details__grid">
            <article>
              <h3>Processing and estimates</h3>
              <p>
                Processing time is separate from carrier transit time. Delivery
                estimates are good-faith estimates, not guaranteed arrival dates,
                unless checkout expressly identifies a guaranteed service.
              </p>
            </article>
            <article>
              <h3>Address accuracy</h3>
              <p>
                Check the recipient name, street address, unit, region, postal
                code, country, and email before paying. Contact us immediately if
                something is wrong; a change cannot be guaranteed after packing.
              </p>
            </article>
            <article>
              <h3>Tracking</h3>
              <p>
                A carrier scan can take time to appear after dispatch. Tracking
                information is supplied by the carrier and may not update at every
                stage of an international journey.
              </p>
            </article>
            <article>
              <h3>Delays and disruption</h3>
              <p>
                Weather, public holidays, customs, labour disruption, security
                checks, local service interruptions, or events outside reasonable
                control can delay a parcel. We will assist with available carrier
                information.
              </p>
            </article>
            <article>
              <h3>Missing or damaged parcels</h3>
              <p>
                Check the delivery location and carrier notice, then contact us
                promptly with the order reference. Keep damaged items and all
                packaging while a carrier or product claim is reviewed.
              </p>
            </article>
            <article>
              <h3>Undeliverable orders</h3>
              <p>
                If a parcel returns because of an incorrect address, refusal,
                missed collection, or unpaid import charge, we will assess the
                available refund after it returns. Unrecoverable delivery and
                return costs may be deducted where the law permits.
              </p>
            </article>
          </div>
        </section>

        <aside className="delivery-help" aria-labelledby="delivery-help-title">
          <div>
            <p className="ink-info-page__kicker">Still need help?</p>
            <h2 id="delivery-help-title">Ask about an order or destination</h2>
          </div>
          <Link className="ink-info-page__text-link" to="/contact">
            Contact ioomparade <span aria-hidden="true">&#8594;</span>
          </Link>
        </aside>

        <p className="commerce-policy-related">
          Cross-border order? <Link to="/policies/international-orders">Read the International Orders &amp; Customs Policy</Link>.
        </p>
      </div>
    </main>
  );
}

export default Delivery;
